import {
    AgentUsageDistributionItemApi,
    DashboardChartPointVm,
    DashboardFilterState,
    DashboardKpiCardVm,
    FeedbackItemApi,
    ManagerStatApi,
    UserAgentQuestionItemApi,
    UsageAnalyticsDashboardVm,
    UsageAnalyticsReportApi,
    UsageAnalyticsResponseApi,
} from './user-dashboard.models';

const numberFormatter = new Intl.NumberFormat('en-US');
const compactDateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
});

export function createDefaultDashboardFilterState(): DashboardFilterState {
    return {
        startDate: '',
        endDate: ''
    };
}

export function createEmptyUsageAnalyticsDashboardVm(): UsageAnalyticsDashboardVm {
    return {
        filterState: createDefaultDashboardFilterState(),
        matchedRows: 0,
        sourceRows: 0,
        kpis: buildKpiCards(0, 0, 0),
        feedbackSentiment: {
            likes: 0,
            dislikes: 0
        },
        lastModifiedTime: '',
        activeUserSeries: [],
        agentUsageItems: [],
        users: [],
        feedbackItems: [],
        allFeedbackItems: [],
        userAgentQuestions: [],
        managerStats: []
    };
}

export function mapUsageAnalyticsResponseToDashboardVm(
    response: UsageAnalyticsResponseApi | null | undefined
): UsageAnalyticsDashboardVm {
    const report = response?.report;

    if (!report) {
        return createEmptyUsageAnalyticsDashboardVm();
    }

    // Neutral feedback (no explicit sentiment) is counted as a like for reporting purposes.
    const likes = safeNumber(report.like_vs_dislike?.likes) + safeNumber(report.like_vs_dislike?.neutral);
    const dislikes = safeNumber(report.like_vs_dislike?.dislikes);
    const allFeedbackItems = buildAllFeedbackItems(report.questions_with_feedback);
    const feedbackItems = buildFeedbackItemsForDialog(allFeedbackItems);
    const userAgentQuestions = buildUserAgentQuestions(report.user_agent_questions);

    return {
        filterState: {
            startDate: (report.filters?.start_date || '').trim(),
            endDate: (report.filters?.end_date || '').trim()
        },
        matchedRows: safeNumber(report.matched_rows),
        sourceRows: safeNumber(report.filters?.source_rows),
        kpis: buildKpiCards(
            safeNumber(report.total_active_users),
            safeNumber(report.total_questions_asked),
            allFeedbackItems.length
        ),
        feedbackSentiment: {
            likes,
            dislikes
        },
        lastModifiedTime: (report.last_modified_time || report._blob_last_modified || '').trim(),
        activeUserSeries: buildActiveUserSeries(report),
        agentUsageItems: buildAgentUsageItems(report.agent_usage_distribution),
        users: report.users || [],
        feedbackItems,
        allFeedbackItems,
        userAgentQuestions,
        managerStats: buildManagerStats(report.manager_stats)
    };
}

function buildManagerStats(items: ManagerStatApi[] | undefined | null): ManagerStatApi[] {
    if (!Array.isArray(items)) {
        return [];
    }

    return [...items]
        .map((item) => ({
            manager_name: (item?.manager_name || '').trim(),
            manager_email: (item?.manager_email || '').trim(),
            total_users_under_manager: safeNumber(item?.total_users_under_manager),
            active_users: safeNumber(item?.active_users),
            total_feedback_count: safeNumber(item?.total_feedback_count)
        }))
        .sort((left, right) => right.active_users - left.active_users);
}

function buildKpiCards(
    totalActiveUsers: number,
    totalQuestionsAsked: number,
    totalFeedbackCount: number
): DashboardKpiCardVm[] {
    return [
        {
            label: 'Total Active Users',
            value: numberFormatter.format(totalActiveUsers),
            subtext: 'Unique users active in the selected range',
            clickAction: 'openActiveUsers'
        },
        {
            label: 'Total Questions Asked',
            value: numberFormatter.format(totalQuestionsAsked),
            subtext: 'Total chatbot questions in the selected range',
            clickAction: 'openQuestions'
        },
        {
            label: 'Questions with Feedback',
            value: numberFormatter.format(totalFeedbackCount),
            subtext: 'Questions that received user feedback',
            clickAction: 'openFeedback'
        }
    ];
}

function buildActiveUserSeries(report: UsageAnalyticsReportApi): DashboardChartPointVm[] {
    const orderedSeries = buildOrderedDateEntries(
        report.daily_active_users,
        report.filters?.start_date || undefined,
        report.filters?.end_date || undefined
    );

    return orderedSeries.map(([isoDate, value]) => ({
        isoDate,
        label: formatDateLabel(isoDate),
        value: safeNumber(value)
    }));
}

function buildAllFeedbackItems(items: FeedbackItemApi[] | undefined | null): FeedbackItemApi[] {
    if (!Array.isArray(items)) return [];
    return [...items];
}

function buildFeedbackItemsForDialog(items: FeedbackItemApi[] | undefined | null): FeedbackItemApi[] {
    if (!Array.isArray(items)) return [];
    // Only keep items where additional_comments is a non-empty string
    return items.filter(item =>
        typeof item?.feedback?.additional_comments === 'string' &&
        item.feedback.additional_comments.trim().length > 0
    );
}

function buildUserAgentQuestions(userAgentQuestionsData: Record<string, Record<string, string[]>> | undefined | null): UserAgentQuestionItemApi[] {
    if (!userAgentQuestionsData || typeof userAgentQuestionsData !== 'object') {
        return [];
    }

    const questions: UserAgentQuestionItemApi[] = [];

    // Iterate through each user email
    for (const [userEmail, agentCategories] of Object.entries(userAgentQuestionsData)) {
        if (!agentCategories || typeof agentCategories !== 'object') continue;

        // Iterate through each agent category for this user
        for (const [agentCategory, questionsList] of Object.entries(agentCategories)) {
            if (!Array.isArray(questionsList)) continue;

            // Add each question with user and agent category info
            questionsList.forEach(question => {
                if (typeof question === 'string' && question.trim()) {
                    questions.push({
                        question: question.trim(),
                        user_email: userEmail.trim(),
                        user_name: '', // Will be populated from users list if available
                        agent_category: agentCategory.trim()
                    });
                }
            });
        }
    }

    return questions;
}

function buildAgentUsageItems(items: AgentUsageDistributionItemApi[] | undefined | null): AgentUsageDistributionItemApi[] {
    if (!Array.isArray(items)) {
        return [];
    }

    return [...items]
        .map((item) => ({
            agent: (item?.agent || 'Unknown Agent').trim() || 'Unknown Agent',
            count: safeNumber(item?.count ?? item?.questions_count),
            percentage: Number.isFinite(Number(item?.percentage)) ? Number(item.percentage) : 0
        }))
        .sort((left, right) => right.count - left.count);
}

function buildOrderedDateEntries(
    source: Record<string, number> | undefined,
    startDate?: string,
    endDate?: string
): Array<[string, number]> {
    const entries = Object.entries(source || {})
        .filter(([isoDate]) => isIsoDate(isoDate))
        .sort(([left], [right]) => left.localeCompare(right));

    if (!entries.length && (!isIsoDate(startDate) || !isIsoDate(endDate))) {
        return [];
    }

    if (isIsoDate(startDate) && isIsoDate(endDate)) {
        const start = parseIsoDateToUtc(startDate);
        const end = parseIsoDateToUtc(endDate);

        if (!start || !end) {
            return [];
        }

        const dayDiff = Math.floor((end.getTime() - start.getTime()) / 86400000);

        if (dayDiff >= 0 && dayDiff <= 366) {
            const lookup = new Map(entries);
            const filledEntries: Array<[string, number]> = [];
            const cursor = new Date(start);

            while (cursor <= end) {
                const isoDate = formatIsoDateFromUtc(cursor);
                if (!isWeekendUtc(cursor)) {
                    filledEntries.push([isoDate, safeNumber(lookup.get(isoDate))]);
                }
                cursor.setUTCDate(cursor.getUTCDate() + 1);
            }

            return filledEntries;
        }
    }

    return entries
        .filter(([isoDate]) => {
            const parsedDate = parseIsoDateToUtc(isoDate);
            return !!parsedDate && !isWeekendUtc(parsedDate);
        })
        .map(([isoDate, value]) => [isoDate, safeNumber(value)]);
}

function isWeekendUtc(date: Date): boolean {
    const day = date.getUTCDay();
    return day === 0 || day === 6;
}

function formatDateLabel(isoDate: string): string {
    const parsedDate = parseIsoDateToUtc(isoDate);

    if (!parsedDate) {
        return isoDate;
    }

    return compactDateFormatter.format(parsedDate);
}

function parseIsoDateToUtc(value: string): Date | null {
    if (!isIsoDate(value)) {
        return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}

function formatIsoDateFromUtc(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function isIsoDate(value: string | null | undefined): value is string {
    return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function safeNumber(value: unknown): number {
    const normalizedValue = Number(value);
    return Number.isFinite(normalizedValue) ? normalizedValue : 0;
}
